import { useState } from 'react';
import {
  Squares2X2Icon,
  UsersIcon,
  IdentificationIcon,
  BanknotesIcon,
  BriefcaseIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { usePendingOfficers } from '../../Users/hooks/usePendingOfficers';
import { ROLES } from '../../../shared/constant/roles';
import { confirmAction } from '../../../shared/utils/dialogs';
import DashboardHome from './DashboardHome';
import StudentsPage from '../../Students';
import PaymentsPage from '../../Payments';
import ProjectsPage from '../../Projects';
import UsersPage from '../../Users';
import SettingsPage from '../../Settings';

const navTabs = [
  { label: 'Dashboard', icon: Squares2X2Icon, component: DashboardHome },
  { label: 'Students', icon: IdentificationIcon, component: StudentsPage },
  { label: 'Payments', icon: BanknotesIcon, component: PaymentsPage },
  { label: 'Disbursements', icon: BriefcaseIcon, component: ProjectsPage },
  { label: 'Users', icon: UsersIcon, component: UsersPage, adminOnly: true },
  { label: 'Settings', icon: Cog6ToothIcon, component: SettingsPage },
];

const gridColsClass = { 5: 'grid-cols-5', 6: 'grid-cols-6' };

function AdminDashboard() {
  const { signOut, role } = useAuth();
  const canSeeNotifications = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const canManageUsers = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Dashboard');
  const { officers: pendingOfficers, approve, reject } = usePendingOfficers(canSeeNotifications);

  const handleLogout = async () => {
    const confirmed = await confirmAction({
      title: 'Log out?',
      text: 'You will need to sign in again to access the dashboard.',
      confirmText: 'Log out',
      danger: true,
    });
    if (confirmed) signOut();
  };

  const visibleNavTabs = navTabs.filter((tab) => !tab.adminOnly || canManageUsers);
  const ActiveComponent = navTabs.find((tab) => tab.label === activeTab)?.component ?? DashboardHome;

  return (
    <div className="h-dvh w-screen overflow-hidden flex flex-col bg-appbg">
      <header className="shrink-0 bg-navy border-b-2 border-gold/40 pl-4 pr-2 py-4 flex justify-between items-center pr-6">
        <div>
          <p className="text-xs font-bold text-gold/70">CALAUAG NATIONAL HIGH SCHOOL</p>
          <h1 className="text-2xl font-semibold text-white">
            {role === ROLES.OFFICER ? 'SPTA Officer Panel' : 'SPTA Admin Panel'}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {canSeeNotifications && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsNotifOpen((prev) => !prev)}
              className="relative p-2 text-white hover:text-navy rounded-full hover:bg-gold transition"
              aria-label="Notifications"
            >
              <BellIcon className="h-6 w-6" />
              {pendingOfficers.length > 0 && (
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-danger" />
              )}
            </button>

            {isNotifOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsNotifOpen(false)}
                />
                <div className="fixed inset-x-3 top-20 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 max-h-[70vh] sm:max-h-96 overflow-y-auto bg-white rounded-lg border border-gray-200 shadow-lg z-20">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900">Pending Officer Approvals</h2>
                  </div>
                  {pendingOfficers.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications yet</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {pendingOfficers.map(({ auth_id, firstname, lastname, role: officerRole, email }) => (
                        <li key={auth_id} className="px-4 py-3 text-sm bg-gold/10">
                          <p className="text-ink">
                            <span className="font-medium">{firstname} {lastname}</span> signed up as {officerRole || 'officer'}
                          </p>
                          <p className="text-xs text-muted mt-0.5">{email}</p>
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => approve(auth_id, `${firstname} ${lastname}`)}
                              className="flex items-center gap-1 rounded-md bg-success text-white text-xs font-medium px-2.5 py-1.5 hover:bg-success/90"
                            >
                              <CheckIcon className="h-3.5 w-3.5" />
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const confirmed = await confirmAction({
                                  title: 'Reject signup?',
                                  text: `Reject ${firstname} ${lastname}'s signup? This cannot be undone.`,
                                  confirmText: 'Reject',
                                  danger: true,
                                });
                                if (confirmed) reject(auth_id, `${firstname} ${lastname}`);
                              }}
                              className="flex items-center gap-1 rounded-md bg-danger text-white text-xs font-medium px-2.5 py-1.5 hover:bg-danger/90"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                              Reject
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
          )}

          {/* Logout, desktop only */}
          <button
            type="button"
            onClick={handleLogout}
            className="hidden xl:flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold text-white bg-danger transition hover:bg-danger/80"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Logout
          </button>

          {/* Hamburger trigger, mobile only */}
          <button
            type="button"
            onClick={() => setIsMenuOpen(true)}
            className="xl:hidden p-2 text-white hover:text-navy rounded-full hover:bg-gold transition"
            aria-label="Open menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
      </header>

      {/* Top menu tabs, desktop/large screens only */}
      <nav className="hidden xl:flex shrink-0 bg-navy-light border-b shadow-md border-navy px-2 justify-center gap-1 overflow-x-auto">
        {visibleNavTabs.map(({ label, icon: Icon }) => {
          const isActive = activeTab === label;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setActiveTab(label)}
              className={`flex items-center gap-1.5 px-4 py-3 text-md border-b-4 whitespace-nowrap transition ${
                isActive
                  ? 'border-gold text-gold font-bold'
                  : 'border-transparent text-white/80 hover:text-gold'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </nav>

      <div className="flex-1 w-full overflow-y-auto pb-16 xl:pb-0">
        <main className="p-4 space-y-4 lg:w-3/4 lg:mx-auto">
          <ActiveComponent onNavigate={setActiveTab} />
        </main>
      </div>

      {/* Bottom tab bar, mobile only, native-app style */}
      <nav
        className={`xl:hidden shrink-0 grid ${gridColsClass[visibleNavTabs.length] ?? 'grid-cols-6'} border-t border-white/10 bg-navy pb-[env(safe-area-inset-bottom)]`}
      >
        {visibleNavTabs.map(({ label, icon: Icon }) => {
          const isActive = activeTab === label;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setActiveTab(label)}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 text-xs font-medium transition ${
                isActive ? 'text-gold' : 'text-white/50 hover:text-gold'
              }`}
            >
              <Icon className="h-6 w-6" />
              {label}
            </button>
          );
        })}
      </nav>

      {/* Slide-in menu: backdrop + right-side drawer */}
      <div className={`fixed inset-0 z-40 ${isMenuOpen ? '' : 'pointer-events-none'}`}>
        {/* Dimmed backdrop, closes the menu on click */}
        <div
          className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMenuOpen(false)}
        />
        {/* Drawer panel, slides in from the right */}
        <div
          className={`absolute top-0 right-0 h-full w-64 bg-navy shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">
              {role === ROLES.OFFICER ? 'Officer Menu' : 'Admin Menu'}
            </h2>
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              className="p-1 text-white"
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Logout, pinned to the bottom of the drawer */}
          <div className="mt-auto px-5 pb-3">
            <button
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                handleLogout();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-full py-3 text-white bg-danger transition hover:bg-danger/80"
            >
              <ArrowRightOnRectangleIcon className="h-6 w-6" />
              Logout
            </button>
          </div>

          {/* Version footer, pinned to the bottom of the drawer */}
          <p className="px-5 py-4 text-xs text-center text-white/40 border-t border-white/10">v1.0</p>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
